import { useEffect } from "react";

import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Track, Participant } from "twilio-video";

import { useAppState } from "../../../../context/StateContext";
import { Comment } from "../../../../models/comment";
import { RoomJob } from "../../../../models/sales";
import { Message } from "../../../../models/video.survey.message";
import VideoSurveyMessageService from "../../../../services/video.survey.messages.service";
import { NotificationToast } from "../../../Generic/Toast/NotificationToast";
import useVideoContext from "../useVideoContext/useVideoContext";

enum MessageType {
  MESSAGE = "message",
  INVENTORY = "inventory",
  ESTIMATE = "estimate",
}

export default function useMessages(participant?: Participant) {
  const { dataTrack, setMessages, task } = useVideoContext();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const { mutate: createVideoSurveyMessage } = useMutation(
    ["video.survey.messages"],
    (message: Message) => VideoSurveyMessageService.create(message)
  );

  const tryParseJsonObject = (data: string) => {
    try {
      const parsedJson = JSON.parse(data);

      return parsedJson;
    } catch (e) {}

    return false;
  };

  useEffect(() => {
    if (participant) {
      const trackSubscribed = (track: Track) => {
        if (track.kind === "data") {
          const onReceiveMessage = (data: string) => {
            const parsedJson = tryParseJsonObject(data);

            if (parsedJson) {
              NotificationToast.info(translate("newMessage"), {
                id: "message.notification",
              });
              setMessages((state) => [...state, parsedJson]);
            }
          };

          track.on("message", onReceiveMessage);
        }
      };

      participant?.on("trackSubscribed", trackSubscribed);

      return () => {
        participant.off("trackSubscribed", trackSubscribed);
      };
    }
  }, [participant]);

  const sendMessage = (message: Comment) => {
    setMessages((state) => [...state, message]);
    createVideoSurveyMessage({
      identifier: message.identifier,
      message: message.message,
      sendDate: message.createdAt,
      valid: true,
      roomName: task?.roomName,
      jobId: task?.jobId,
      userId: currentUser?.id,
    } as Message);
    dataTrack.send(JSON.stringify({ ...message, type: MessageType.MESSAGE }));
  };

  const shareInventory = (inventory: RoomJob[]) => {
    dataTrack.send(
      JSON.stringify({
        inventory: inventory || [],
        type: MessageType.INVENTORY,
      })
    );
  };

  const shareEstimate = () => {
    dataTrack.send(JSON.stringify({ type: MessageType.ESTIMATE }));
  };

  return { sendMessage, shareInventory, shareEstimate };
}
